import { node, isFocusable, findFirstFocusableElement } from "~/util/dom";
import { startFocusTrap } from "~/util/focustrap";
import { useEscapeKey } from "~/util/hooks";
import { computePosition, autoUpdate, Placement, shift, offset, flip } from "@floating-ui/dom";

export interface IPopOverProps {
	anchor: HTMLElement;
	popOverParent: HTMLElement;
	activator: HTMLElement;
	position: Placement;
	offset: number;
	content: HTMLElement | DocumentFragment;
	width: number;
	className: string;
	clickOutClose: boolean;
	escapeClose: boolean;
	clickOutIgnoredElements: Array<Element>;
	onClose: () => void;
	onOpen: () => void;
	focusTrap: boolean;
}

export const DEFAULT_PROPS: IPopOverProps = {
	anchor: null,
	popOverParent: document.body,
	activator: null,
	position: "bottom",
	offset: 0,
	content: null,
	width: 0,
	className: "",
	clickOutClose: true,
	escapeClose: true,
	clickOutIgnoredElements: [],
	onClose: null,
	onOpen: null,
	focusTrap: true,
};

export default class PopOver {
	protected popOver: HTMLDivElement;

	protected props: IPopOverProps;

	protected _isOpen: boolean;

	protected _cleanupEsc: () => void;

	protected _cleanupAutoUpdate: () => void;

	protected _stopFocusTrap: ReturnType<typeof startFocusTrap>;

	constructor(props: Partial<IPopOverProps> & Pick<IPopOverProps, "anchor">) {
		this.popOver = node("div", { className: `ui-popover ${props.className || ""}` });
		props.content && this.popOver.appendChild(props.content);

		this.props = {
			...DEFAULT_PROPS,
			activator: document.activeElement as HTMLElement,
			...props,
			clickOutIgnoredElements: [
				this.popOver,
				// pokud v props neprislo, ktere elementy ignorovat na clickOut, pak predpokladame nejtypictejsi chovani, tedy ignorovat anchor, coz byva ten, kdo akci vyvolal
				..."clickOutIgnoredElements" in props ? props.clickOutIgnoredElements : [props.anchor],
			],
		};
		this._isOpen = false;
	}

	get isOpen() {
		return this._isOpen;
	}

	get container() {
		return this.popOver;
	}

	destroy() {
		this.close();
	}

	handleEvent(event: PointerEvent) {
		switch (event.type) {
			case "pointerdown": {
				const target = event.target as Element;
				let isInIgnored = false;

				for (let index = 0; index < this.props.clickOutIgnoredElements.length; index++) {
					const ignored = this.props.clickOutIgnoredElements[index];

					if (ignored.contains(target)) {
						isInIgnored = true;
					}
				}

				if (!isInIgnored) {
					this.close();
				}

				break;
			}

			default:
				//
		}
	}

	close() {
		if (!this._isOpen) {
			return;
		}

		this._isOpen = false;
		this.popOver.remove();
		this.props.clickOutClose && window.removeEventListener("pointerdown", this, true);
		this._cleanupEsc?.();
		this._cleanupAutoUpdate?.();
		this._stopFocusTrap?.();
		this._stopFocusTrap = undefined;
		this.props.onClose?.();
	}

	open() {
		if (this._isOpen) {
			return;
		}

		this._isOpen = true;
		this.props.onOpen?.();

		this._cleanupAutoUpdate = autoUpdate(this.props.anchor, this.popOver, () => {
			const middleware = [offset(this.props.offset)];
			const flipMiddleware = flip({
				crossAxis: "alignment",
				fallbackAxisSideDirection: "end",
			});
			const shiftMiddleware = shift({
				crossAxis: true,
			});

			if (this.props.position.includes("-")) {
				middleware.push(flipMiddleware, shiftMiddleware);
			} else {
				middleware.push(shiftMiddleware, flipMiddleware);
			}

			this.props.popOverParent.appendChild(this.popOver);
			computePosition(this.props.anchor, this.popOver, {
				placement: this.props.position,
				middleware,
			}).then(({ x, y, placement }) => {
				Object.assign(this.popOver.style, {
					left: `${x}px`,
					top: `${y}px`,
				});
				this.popOver.classList[placement === "bottom-end" ? "add" : "remove"]("ui-popover--bottom-end");
				this.popOver.classList[placement === "bottom-start" ? "add" : "remove"]("ui-popover--bottom-start");
				this.popOver.classList[placement === "top-end" ? "add" : "remove"]("ui-popover--top-end");
				this.popOver.classList[placement === "top-start" ? "add" : "remove"]("ui-popover--top-start");
				findFirstFocusableElement(this.popOver)?.focus();
			});
		});

		// klik kamkoliv mimo popover ho zavira
		this.props.clickOutClose && setTimeout(() => {
			window.addEventListener("pointerdown", this, true);
		}, 0);

		// escape zavira
		if (this.props.escapeClose) {
			this._cleanupEsc = useEscapeKey(document, () => {
				this.close();
				this.props.activator && isFocusable(this.props.activator) && this.props.activator.focus();
			});
		}

		// focusneme prvniho potomka, ktery to umi
		setTimeout(() => {
			(document.activeElement as HTMLElement)?.blur?.();

			if (this.props.focusTrap) {
				this._stopFocusTrap = startFocusTrap(this.popOver);
			}
		}, 0);
	}

	toggle() {
		if (this._isOpen) {
			this.close();
		} else {
			this.open();
		}
	}
}
